import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateOrganization } from '@/graphql/mutations/organizations';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import {
  CheckCircle2,
  Shield,
  ExternalLink,
  Loader2,
  Trash2,
  Check,
  Eye,
  EyeOff,
  Copy,
} from 'lucide-react';
import type { Organization } from '../../types/org.types';

interface IntegrationsSettingsProps {
  org: Organization;
}

export const IntegrationsSettings = ({ org }: IntegrationsSettingsProps) => {
  const { t } = useTranslation();
  const updateOrg = useUpdateOrganization();

  const hasCredentials = !!(org.SeliseClientId && org.SeliseClientSecret);
  const [showCredentialForm, setShowCredentialForm] = useState(false);
  const [isRemovingCredentials, setIsRemovingCredentials] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showExistingClientId, setShowExistingClientId] = useState(false);
  const [showExistingClientSecret, setShowExistingClientSecret] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showClientId, setShowClientId] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);

  const handleCopy = (value: string, field: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const maskValue = (value: string) => {
    if (value.length <= 8) return '••••••••';
    return value.slice(0, 4) + '••••••••' + value.slice(-4);
  };
  const [credentials, setCredentials] = useState({
    seliseClientId: '',
    seliseClientSecret: '',
  });

  const handleSaveCredentials = async () => {
    if (!credentials.seliseClientId || !credentials.seliseClientSecret) {
      setError(t('ORGSETTINGS_INTEGRATIONS_ERRORS_MISSINGCREDENTIALS'));
      return;
    }

    setError(null);

    try {
      await updateOrg.mutateAsync({
        itemId: org.ItemId,
        SeliseClientId: credentials.seliseClientId,
        SeliseClientSecret: credentials.seliseClientSecret,
      });

      setSuccess(true);
      setShowCredentialForm(false);
      setCredentials({ seliseClientId: '', seliseClientSecret: '' });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('ORGSETTINGS_INTEGRATIONS_ERRORS_SAVEFAILED')
      );
    }
  };

  const handleRemoveCredentials = async () => {
    setIsRemovingCredentials(true);
    setError(null);

    try {
      await updateOrg.mutateAsync({
        itemId: org.ItemId,
        SeliseClientId: '',
        SeliseClientSecret: '',
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('ORGSETTINGS_INTEGRATIONS_ERRORS_REMOVEFAILED')
      );
    } finally {
      setIsRemovingCredentials(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* SELISE Signature Integration Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-linear-to-br from-selise-blue to-oxford-blue flex items-center justify-center shadow-sm">
                <img
                  src="/signature-black.svg"
                  alt="SELISE Signature"
                  width={28}
                  height={28}
                  className="invert"
                />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  {t('ORGSETTINGS_INTEGRATIONS_CARDTITLE')}
                  {hasCredentials && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-lime-green/10 text-poly-green">
                      <CheckCircle2 className="h-3 w-3" />
                      {t('ORGSETTINGS_INTEGRATIONS_CONNECTEDBADGE')}
                    </span>
                  )}
                </CardTitle>
                <CardDescription>{t('ORGSETTINGS_INTEGRATIONS_CARDDESCRIPTION')}</CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status indicator */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border border-border">
            <Shield className="h-5 w-5 text-globe-grey shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-[hsl(var(--fg))]">
                {hasCredentials
                  ? t('ORGSETTINGS_INTEGRATIONS_STATUS_ORGTITLE')
                  : t('ORGSETTINGS_INTEGRATIONS_STATUS_DEFAULTTITLE')}
              </p>
              <p className="text-sm text-globe-grey">
                {hasCredentials
                  ? t('ORGSETTINGS_INTEGRATIONS_STATUS_ORGDESCRIPTION')
                  : t('ORGSETTINGS_INTEGRATIONS_STATUS_DEFAULTDESCRIPTION')}
              </p>
            </div>
          </div>

          {/* Guide for getting credentials */}
          <div className="p-4 rounded-lg border border-border bg-bg">
            <p className="text-sm font-medium text-[hsl(var(--fg))] mb-3">
              {t('ORGSETTINGS_INTEGRATIONS_GUIDE_TITLE')}
            </p>
            <ol className="text-sm text-globe-grey space-y-2">
              <li className="flex items-start gap-2">
                <span className="flex items-center justify-center h-5 w-5 rounded-full bg-selise-blue/10 text-selise-blue text-xs font-medium shrink-0">
                  1
                </span>
                <span>
                  {t('ORGSETTINGS_INTEGRATIONS_GUIDE_STEP1PREFIX')}{' '}
                  <a
                    href="https://selise.app/developers/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-selise-blue hover:underline inline-flex items-center gap-1"
                  >
                    {t('ORGSETTINGS_INTEGRATIONS_GUIDE_PORTAL')}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex items-center justify-center h-5 w-5 rounded-full bg-selise-blue/10 text-selise-blue text-xs font-medium shrink-0">
                  2
                </span>
                <span>{t('ORGSETTINGS_INTEGRATIONS_GUIDE_STEP2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex items-center justify-center h-5 w-5 rounded-full bg-selise-blue/10 text-selise-blue text-xs font-medium shrink-0">
                  3
                </span>
                <span>{t('ORGSETTINGS_INTEGRATIONS_GUIDE_STEP3')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex items-center justify-center h-5 w-5 rounded-full bg-selise-blue/10 text-selise-blue text-xs font-medium shrink-0">
                  4
                </span>
                <span>{t('ORGSETTINGS_INTEGRATIONS_GUIDE_STEP4')}</span>
              </li>
            </ol>
          </div>

          {/* Current Credentials Display */}
          {hasCredentials && !showCredentialForm && (
            <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/30">
              <p className="text-sm font-medium text-[hsl(var(--fg))]">
                {t('ORGSETTINGS_INTEGRATIONS_CURRENTCREDENTIALS_TITLE')}
              </p>
              <div className="space-y-2">
                <Label className="text-xs text-globe-grey">
                  {t('ORGSETTINGS_INTEGRATIONS_FORM_CLIENTIDLABEL')}
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono bg-bg px-3 py-2 rounded-md border border-border truncate">
                    {showExistingClientId
                      ? org.SeliseClientId || ''
                      : maskValue(org.SeliseClientId || '')}
                  </code>
                  <button
                    type="button"
                    onClick={() => setShowExistingClientId(!showExistingClientId)}
                    className="p-2 rounded-md hover:bg-muted text-globe-grey hover:text-[hsl(var(--fg))]"
                  >
                    {showExistingClientId ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopy(org.SeliseClientId || '', 'clientId')}
                    className="p-2 rounded-md hover:bg-muted text-globe-grey hover:text-[hsl(var(--fg))]"
                  >
                    {copiedField === 'clientId' ? (
                      <Check className="h-4 w-4 text-poly-green" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-globe-grey">
                  {t('ORGSETTINGS_INTEGRATIONS_FORM_CLIENTSECRETLABEL')}
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono bg-bg px-3 py-2 rounded-md border border-border truncate">
                    {showExistingClientSecret
                      ? org.SeliseClientSecret
                      : maskValue(org.SeliseClientSecret || '')}
                  </code>
                  <button
                    type="button"
                    onClick={() => setShowExistingClientSecret(!showExistingClientSecret)}
                    className="p-2 rounded-md hover:bg-muted text-globe-grey hover:text-[hsl(var(--fg))]"
                  >
                    {showExistingClientSecret ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopy(org.SeliseClientSecret || '', 'clientSecret')}
                    className="p-2 rounded-md hover:bg-muted text-globe-grey hover:text-[hsl(var(--fg))]"
                  >
                    {copiedField === 'clientSecret' ? (
                      <Check className="h-4 w-4 text-poly-green" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-lime-green/10 text-poly-green text-sm">
              <Check className="h-4 w-4" />
              {t('ORGSETTINGS_INTEGRATIONS_ALERTS_SUCCESS')}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 rounded-md bg-crimson/10 text-crimson text-sm">{error}</div>
          )}

          {!showCredentialForm ? (
            <div className="flex justify-around flex-wrap gap-3 pt-2">
              <Button
                size={'sm'}
                type="button"
                onClick={() => {
                  if (hasCredentials) {
                    setCredentials({
                      seliseClientId: org.SeliseClientId || '',
                      seliseClientSecret: org.SeliseClientSecret || '',
                    });
                  }
                  setShowCredentialForm(true);
                }}
              >
                {hasCredentials
                  ? t('ORGSETTINGS_INTEGRATIONS_ACTIONS_UPDATECREDENTIALS')
                  : t('ORGSETTINGS_INTEGRATIONS_ACTIONS_CONFIGURECREDENTIALS')}
              </Button>
              {hasCredentials && (
                <Button
                  type="button"
                  variant="outline"
                  className="text-crimson border-crimson/30 hover:bg-crimson/10 hover:text-crimson"
                  onClick={handleRemoveCredentials}
                  disabled={isRemovingCredentials}
                >
                  {isRemovingCredentials ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  {t('ORGSETTINGS_INTEGRATIONS_ACTIONS_REMOVECREDENTIALS')}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4 p-4 rounded-lg border border-selise-blue/20 bg-selise-blue/5">
              <div className="space-y-2">
                <Label htmlFor="seliseClientId">
                  {t('ORGSETTINGS_INTEGRATIONS_FORM_CLIENTIDLABEL')}
                </Label>
                <div className="relative">
                  <Input
                    id="seliseClientId"
                    type={showClientId ? 'text' : 'password'}
                    placeholder={t('ORGSETTINGS_INTEGRATIONS_FORM_CLIENTIDPLACEHOLDER')}
                    value={credentials.seliseClientId}
                    onChange={(e) =>
                      setCredentials({
                        ...credentials,
                        seliseClientId: e.target.value,
                      })
                    }
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowClientId(!showClientId)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-globe-grey hover:text-[hsl(var(--fg))]"
                  >
                    {showClientId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="seliseClientSecret">
                  {t('ORGSETTINGS_INTEGRATIONS_FORM_CLIENTSECRETLABEL')}
                </Label>
                <div className="relative">
                  <Input
                    id="seliseClientSecret"
                    type={showClientSecret ? 'text' : 'password'}
                    placeholder={t('ORGSETTINGS_INTEGRATIONS_FORM_CLIENTSECRETPLACEHOLDER')}
                    value={credentials.seliseClientSecret}
                    onChange={(e) =>
                      setCredentials({
                        ...credentials,
                        seliseClientSecret: e.target.value,
                      })
                    }
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowClientSecret(!showClientSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-globe-grey hover:text-[hsl(var(--fg))]"
                  >
                    {showClientSecret ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <p className="text-xs text-globe-grey">{t('ORGSETTINGS_INTEGRATIONS_FORM_HELPER')}</p>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  onClick={handleSaveCredentials}
                  disabled={updateOrg.isPending}
                >
                  {updateOrg.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('ORGSETTINGS_INTEGRATIONS_ACTIONS_SAVECREDENTIALS')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowCredentialForm(false);
                    setCredentials({
                      seliseClientId: '',
                      seliseClientSecret: '',
                    });
                    setError(null);
                  }}
                >
                  {t('ORGSETTINGS_INTEGRATIONS_ACTIONS_CANCEL')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Placeholder for future integrations */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-dashed opacity-60">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                <span className="text-lg">+</span>
              </div>
              <div>
                <CardTitle className="text-base">
                  {t('ORGSETTINGS_INTEGRATIONS_COMINGSOON_TITLE')}
                </CardTitle>
                <CardDescription className="text-xs">
                  {t('ORGSETTINGS_INTEGRATIONS_COMINGSOON_DESCRIPTION')}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    </div>
  );
};
